import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useTheme } from "@mui/material";
import {
  MAX_NEAREST_SHIFTS_TO_DISPLAY,
  NearestOpenShiftsList,
} from "@src/appV2/redesign/Shift/NearestOpenShifts/NearestOpenShiftsList";
import { useNearestOpenShifts } from "@src/appV2/redesign/Shift/NearestOpenShifts/useNearestOpenShifts";
import { useRefreshOnStageChange } from "@src/appV2/redesign/ShiftDiscovery/ListView/useRefreshOnStageChange";
import { Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { type RefObject, useMemo } from "react";

import { GroupedList } from "../../components/GroupedList";
import { ListLoadingState } from "../../components/ListLoadingState";
import { FortuneCookieBottomSheet, FortuneCookieListCard } from "../../FortuneCookie";
import { InAppAdCard } from "../../Shift/InAppAd/Card";
import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import { ShiftListDateHeader } from "../../Shift/Open/ListDateHeader";
import type { OpenShiftListItem } from "../../Shift/Open/types";
import { convertShiftToSlotId } from "../../Shift/Open/useGetShiftsSlotV2";
import { useOpenShiftListDataContext } from "../../Shift/Open/useOpenShiftListDataContext";
import { useVirtualShiftListContext } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { WorkerPaySurveyCard } from "../../WorkerPaySurvey/components/WorkerPaySurveyCard";
import { useShiftDiscoveryUserFiltersContext } from "../Filters/useUserFiltersContext";
import { usePreFetchGeolocation } from "../usePreFetchGeolocation";
import { ListViewBanners } from "./Banners";
import { FallbackShiftsFooter } from "./FallbackShiftsFooter";
import { FallbackShiftsList } from "./FallbackShiftsList";
import { ListViewPageOpenShiftsEmptyState } from "./ListEmptyState";
import { TopPickedShiftsList } from "./TopPickedShiftsList";
import { useFallbackShifts } from "./useFallbackShifts";
import { useForYouShifts } from "./useForYouShifts";

interface ListViewPageOpenShiftsListProps {
  readonly daysCount: number;
  readonly customScrollParentRef: RefObject<HTMLDivElement>;
}

/**
 * Shows the list of open shifts and worker shifts for the current date
 * We prioritize showing open shifts and don't wait for worker shifts to load since:
 * 1. Worker shifts typically load faster than open shifts
 * 2. Open shifts are the primary content users want to see
 * Therefore we don't show loading/error states for worker shifts
 */
export function ListViewPageOpenShiftsList(props: ListViewPageOpenShiftsListProps) {
  const theme = useTheme();
  const { daysCount, customScrollParentRef } = props;
  const { virtualShiftListItems: items } = useVirtualShiftListContext();
  const worker = useDefinedWorker();
  const isRestricted = worker.stage === WorkerStage.RESTRICTED;
  const { appliedFiltersCount, clearFilters } = useShiftDiscoveryUserFiltersContext();
  const fortuneCookieModalState = useModalState("closed");

  usePreFetchGeolocation();

  useRefreshOnStageChange();

  // Filter out group headers and only keep actual shifts
  // Once we've migrated all the lists to GroupedList we can update the hook directly
  // Additionally, we should eventually filter generically on injected items vs just in-app-ads
  const virtualShiftListItems = useMemo(
    () =>
      items.filter(
        (item): item is OpenShiftListItem =>
          "type" in item &&
          (item.type === "open-shift" ||
            item.type === "in-app-ad" ||
            item.type === "fortune-cookie" ||
            item.type === "worker-pay-survey")
      ),
    [items]
  );

  // Check if there are no open shifts specifically (ignoring in-app ads)
  const openShiftsCount = useMemo(
    () => virtualShiftListItems.filter((item) => item.type === "open-shift").length,
    [virtualShiftListItems]
  );

  const { shouldShowFallbackShifts, fallbackShiftsData } = useFallbackShifts({
    openShiftsCount,
    appliedFiltersCount,
  });

  const adWithNoOpenShifts =
    openShiftsCount === 0
      ? virtualShiftListItems.find((item) => item.type === "in-app-ad")
      : undefined;

  const surveyWithNoOpenShifts =
    openShiftsCount === 0
      ? virtualShiftListItems.find((item) => item.type === "worker-pay-survey")
      : undefined;

  const {
    onClickOpenShift,
    workplacesMap,
    shiftsBlockingRequirementsContextMap,
    shiftsSlotsMap,
    isOpenShiftsLoading,
    isOpenShiftsError,
    isOpenShiftsSuccess,
    canLoadMore,
    loadMore,
    isLoadingMore,
    friendsMap,
    recentColleaguesMap,
    shiftAuctionsMap,
    handleRangeChanged,
    favoriteWorkplaceIds,
  } = useOpenShiftListDataContext();

  const openShifts = useMemo(
    () => virtualShiftListItems.filter((item) => item.type === "open-shift"),
    [virtualShiftListItems]
  );

  const forYouShifts = useForYouShifts({
    shifts: openShifts,
    workplacesMap,
    isOpenShiftsSuccess: isOpenShiftsSuccess ?? false,
  });

  const hasNoOpenShifts = !!isOpenShiftsSuccess && openShiftsCount === 0;
  const hasFewFallbackShifts =
    !shouldShowFallbackShifts ||
    (!fallbackShiftsData.isLoading && fallbackShiftsData.shifts.length < 5);
  const shouldQueryNearestOpenShifts = hasNoOpenShifts && hasFewFallbackShifts;
  const nearestOpenShifts = useNearestOpenShifts({
    enabled: shouldQueryNearestOpenShifts,
    // Due to filtering that happens after DB queries, we pad the number of
    // shifts we want to display.
    pageSize: MAX_NEAREST_SHIFTS_TO_DISPLAY * 3,
  });
  const shouldShowNearestShifts =
    nearestOpenShifts.isSuccess && nearestOpenShifts.shifts.length > 0;

  if (isOpenShiftsError) {
    return <Text>Error loading shifts</Text>;
  }

  if (isOpenShiftsLoading) {
    return (
      <ListLoadingState
        texts={[
          "Finding you the best-paying shifts nearby",
          "Analyzing shift opportunities just for you",
          "Your perfect shift is just a moment away",
          "Crunching the numbers to maximize your earnings",
          "Digging deep into facility schedules",
          "Searching high and low for the best shifts nearby",
          "We are on it-finding shifts that matter most",
          "Unlocking your next great opportunity",
        ]}
      />
    );
  }

  if (isOpenShiftsSuccess && openShiftsCount === 0 && !isRestricted) {
    return (
      <ListViewPageOpenShiftsEmptyState
        daysCount={daysCount}
        inAppAd={adWithNoOpenShifts}
        workerPaySurveyId={surveyWithNoOpenShifts?.surveyId}
        unfilteredShiftsLength={fallbackShiftsData.shifts.length}
        onClearFilters={clearFilters}
      >
        {shouldShowFallbackShifts && (
          <FallbackShiftsList
            favoriteWorkplaceIds={favoriteWorkplaceIds}
            shifts={fallbackShiftsData.shifts ?? []}
            workplacesMap={fallbackShiftsData.workplacesMap ?? new Map()}
            onClickOpenShift={onClickOpenShift}
          />
        )}
        {shouldShowNearestShifts && (
          <NearestOpenShiftsList
            favoriteWorkplaceIds={favoriteWorkplaceIds}
            shifts={nearestOpenShifts.shifts}
            workplacesMap={nearestOpenShifts.workplacesMap}
            onClickOpenShift={onClickOpenShift}
          />
        )}
      </ListViewPageOpenShiftsEmptyState>
    );
  }

  return (
    <>
      <GroupedList
        customScrollParentRef={customScrollParentRef}
        renderListHeader={() => (
          <>
            <ListViewBanners />
            <TopPickedShiftsList shifts={forYouShifts} scrollContainerRef={customScrollParentRef} />
          </>
        )}
        renderListFooter={() => {
          if (shouldShowFallbackShifts) {
            return (
              <FallbackShiftsFooter
                favoriteWorkplaceIds={favoriteWorkplaceIds}
                shifts={fallbackShiftsData.shifts ?? []}
                workplacesMap={fallbackShiftsData.workplacesMap ?? new Map()}
                onClickOpenShift={onClickOpenShift}
                onClearFilters={clearFilters}
              />
            );
          }

          return null;
        }}
        items={virtualShiftListItems}
        isLoadingMore={isLoadingMore}
        groupBy={(shift) =>
          formatInTimeZone(
            new Date(shift.type === "open-shift" ? shift.attributes.start : (shift.start ?? "")),
            worker.tmz,
            "yyyy-MM-dd"
          )
        }
        backgroundColor={theme.palette.background.tertiary}
        renderGroupHeader={(dateIso, count) => (
          <ShiftListDateHeader
            key={dateIso}
            label={formatDayOfMonthWithDayName(parseISO(dateIso))}
            totalShiftsCount={count}
            sx={{
              px: 5,
            }}
          />
        )}
        renderItem={(shift) => {
          if (shift.type === "in-app-ad") {
            return <InAppAdCard inAppAd={shift} sx={{ mx: 5 }} />;
          }

          if (shift.type === "fortune-cookie") {
            return (
              <FortuneCookieListCard
                onClick={() => {
                  fortuneCookieModalState.openModal();
                }}
              />
            );
          }

          if (shift.type === "worker-pay-survey") {
            return <WorkerPaySurveyCard surveyId={shift.surveyId} sx={{ mx: 5 }} />;
          }

          const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
          if (!isDefined(workplace)) {
            return null;
          }

          const shiftAuctionId = shift.relationships.shiftAuction?.data.id;
          const shiftAuction = shiftAuctionId ? shiftAuctionsMap.get(shiftAuctionId) : undefined;

          const isTopThreeShift = openShifts
            .slice(0, 3)
            .some((shiftItem) => shiftItem.id === shift.id);

          return (
            <OpenShiftCardContainer
              key={shift.id}
              isFavoriteWorkplace={favoriteWorkplaceIds.has(workplace.id)}
              shift={shift}
              workplace={workplace}
              blockingRequirementsContext={
                shiftsBlockingRequirementsContextMap.get(shift.id) ?? undefined
              }
              workers={
                shiftsSlotsMap
                  .get(
                    convertShiftToSlotId({
                      start: shift.attributes.start,
                      end: shift.attributes.end,
                      facilityId: workplace.id,
                    })
                  )
                  ?.shiftAssignments.filter((assignment) => assignment.shiftId !== shift.id)
                  .map(({ worker }) => worker) ?? []
              }
              friendsMap={friendsMap}
              recentColleaguesMap={recentColleaguesMap}
              shiftAuction={shiftAuction}
              sx={{
                mx: 5,
              }}
              isTopThreeShift={isTopThreeShift}
              onClick={({ offer }) => {
                onClickOpenShift({
                  shift,
                  workplace,
                  offer,
                  shiftAuction,
                  isTopThreeShift,
                });
              }}
            />
          );
        }}
        endReached={() => {
          if (canLoadMore) {
            loadMore();
          }
        }}
        rangeChanged={handleRangeChanged}
        loadingText="Loading more shifts..."
        scrollerTestId="list-view-page-scroll-container"
      />

      <FortuneCookieBottomSheet modalState={fortuneCookieModalState} />
    </>
  );
}
